import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import MedioPagoModal from "../../components/MedioPagoModal";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";
import VentasPorCliente from "./VentasPorCliente";
import VentasPorPieza from "./VentasPorPieza";
import Cuadre from "./Cuadre";
import ClasificarCompras from "./ClasificarCompras";
import Pagos from "./Pagos";
import Cartolas from "./Cartolas";
import Libro from "./Libro";
import CorreoContabilidad from "./CorreoContabilidad";
import ActivosFijos from "./ActivosFijos";
import CostosResumen from "./CostosResumen";
import Finanzas from "./Finanzas";

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmtM = v => v != null ? `$${formatPrice(v)}` : "—";
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day:"2-digit", month:"short", year:"numeric" }) : "—";

const ESTADO_STYLE = {
  acusado:    { label:"Acusado",   bg:"#0d2a15", color:"#2ecc71", border:"#0d4020" },
  pendiente:  { label:"Pendiente", bg:"#2d2000", color:"#f39c12", border:"#4a3500" },
  reclamado:  { label:"Reclamado", bg:"#2d1515", color:"#e74c3c", border:"#4a2020" },
};

const Badge = ({ estado }) => {
  const s = ESTADO_STYLE[estado] || { label: estado || "—", bg:"#222", color: "#aaa", border:"#333" };
  return (
    <span style={{
      display:"inline-block", fontSize:10, fontWeight:700,
      padding:"2px 8px", borderRadius:20, whiteSpace:"nowrap",
      background:s.bg, color:s.color, border:`1px solid ${s.border}`,
    }}>{s.label}</span>
  );
};

// Pestañas válidas para el deep-link ?tab= (dashboard "Pulso" y enlaces internos)
const TABS_VALIDAS = ["ventas", "compras", "clasificar", "clientes", "piezas", "cuadre", "pagos", "cartolas", "libro", "activos", "finanzas", "costos", "correo"];

const S = {
  wrap: {
    display:"block", padding:"clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background:"#1a1a1a",
    minHeight:"100%", color:"#ddd", fontFamily:"Arial,sans-serif",
    fontSize:13, textAlign:"left", boxSizing:"border-box", width:"100%", overflowY:"auto",
  },
  th: {
    background:"#1a1a1a", padding:"7px 10px", textAlign:"left",
    fontSize:10, fontWeight:700, color: "#999",
    borderBottom:"1px solid #2e2e2e", textTransform:"uppercase", letterSpacing:".04em",
  },
  td: {
    padding:"7px 10px", color:"#bbb",
    borderBottom:"1px solid #252525",
    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
  },
  // Estándar visual 28-ago: tarjeta #232323, borde #2e2e2e, radio 12
  card: { background:"#232323", border:"1px solid #2e2e2e", borderRadius:12, padding:"12px 14px" },
  cardLabel: { fontSize:10, fontWeight:700, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:".05em" },
  input: {
    background:"#161616", border:"1px solid #333", borderRadius:5,
    padding:"7px 10px", fontSize:12, color:"#ddd",
    fontFamily:"Arial,sans-serif", boxSizing:"border-box",
  },
  tab: (active) => ({
    padding:"7px 16px", fontSize:11, fontWeight:700, cursor:"pointer",
    border:"none", borderRadius:5,
    background: active ? "#e30613" : "transparent",
    color: active ? "#fff" : "#999",
    whiteSpace: "nowrap", flexShrink: 0,
  }),
};

// ─── Componente principal ─────────────────────────────────────────────────────

const Contabilidad = () => {
  useTitle("Contabilidad");
  // Rama móvil (26-ago): tarjetas en vez de tabla, controles a lo ancho.
  const isMobile = useIsMobile();

  const now = new Date();
  const [mes,  setMes]  = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [ano,  setAno]  = useState(String(now.getFullYear()));
  const [tab,  setTab]  = useState("ventas");

  // Deep-links (28-ago, auditoría UX): /contabilidad?tab=…&filtro=sin-vincular&buscar=folio
  // Los usan el Pulso del Dashboard y "ver documento" de las excepciones del Libro.
  const [searchParams] = useSearchParams();
  // Filtros de la tabla de facturas (en cliente)
  const [buscar, setBuscar] = useState("");                    // folio / razón social / RUT
  const [estadoFiltro, setEstadoFiltro] = useState(null);      // acusado | pendiente | reclamado | null
  const [soloSinVincular, setSoloSinVincular] = useState(false);
  // Mini-modal medio de pago (reemplaza el window.prompt)
  const [pagoModal, setPagoModal] = useState(null);            // factura de compra a marcar pagada

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS_VALIDAS.includes(t)) setTab(t);
    if (searchParams.get("filtro") === "sin-vincular") setSoloSinVincular(true);
    const b = searchParams.get("buscar");
    if (b !== null) setBuscar(b);
  }, [searchParams]);

  const [estado,   setEstado]   = useState(null);   // { conectado, rut, ultimaSync, cupo:{usadas,limite,bloqueoBoton,botonBloqueado} }
  const [facturas, setFacturas] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [syncing,  setSyncing]  = useState(false);
  const [syncMsg,  setSyncMsg]  = useState(null);

  const [selectedF, setSelectedF] = useState(null);  // factura seleccionada (panel)
  const [workOrders, setWorkOrders] = useState([]);   // OTs del período para vincular
  // Fase B (26-ago): repartos = [{workOrderId, monto}] — una factura puede
  // repartir su NETO entre varias OTs. Si va vacío, rige workOrderId (legacy).
  const [vincForm, setVincForm]  = useState({ workOrderId:"", ocCliente:"", guias:"", repartos: [] });
  const [vincSaving, setVincSaving] = useState(false);
  // 27-ago: emisión de NOTA DE CRÉDITO electrónica (61) sobre una factura de
  // venta 33 nuestra, vía SimpleFactura. { codigo, motivo, montoNeto, enviando, resultado, error }
  const [ncModal, setNcModal] = useState(null);

  const periodo = `${ano}-${mes}`;

  const loadEstado = useCallback(async () => {
    try {
      const r = await backend.get("sii/estado");
      setEstado(r.data);
    } catch { setEstado({ conectado: false }); }
  }, []);

  const loadFacturas = useCallback(async () => {
    if (["clientes", "piezas", "cuadre", "pagos", "cartolas", "libro", "activos", "finanzas", "clasificar", "costos"].includes(tab)) return; // esas vistas cargan lo suyo
    setLoading(true);
    try {
      const r = await backend.get(`sii/facturas?periodo=${periodo}&tipo=${tab === "ventas" ? "venta" : "compra"}`);
      setFacturas(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [periodo, tab]);

  useEffect(() => { loadEstado(); }, [loadEstado]);
  useEffect(() => { loadFacturas(); }, [loadFacturas]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await backend.post("sii/sync", { mes, ano });
      const cupo = r.data.cupo ? ` · SimpleAPI ${r.data.cupo.usadas}/${r.data.cupo.limite} este mes` : "";
      setSyncMsg(r.data.cache
        ? `✅ ${r.data.mensaje}`
        : `✅ ${r.data.sincronizadas} facturas sincronizadas para ${periodo}${cupo}`);
      await loadFacturas();
    } catch (e) {
      // 429 = cupo mensual de SimpleAPI (el backend manda el mensaje completo).
      setSyncMsg(`${e?.response?.status === 429 ? "⛔" : "❌"} ${e?.response?.data?.error || e.message}`);
    }
    await loadEstado(); // refresca el contador de cupo
    setSyncing(false);
  };

  const openPanel = async (f) => {
    setSelectedF(f);
    setVincForm({
      workOrderId: f.workOrder?._id || "",
      ocCliente:   f.ocCliente || "",
      guias:       (f.guiasDespacho || []).join(", "),
      repartos:    (f.repartos || []).map(r => ({
        workOrderId: r.workOrder?._id || r.workOrder || "",
        monto: String(r.monto || ""),
      })),
    });
    try {
      const r = await backend.get(`sii/workorders?periodo=${periodo}`);
      setWorkOrders(r.data || []);
    } catch { setWorkOrders([]); }
  };

  const handleVincular = async () => {
    if (!selectedF) return;
    setVincSaving(true);
    try {
      // Se manda "" / [] EXPLÍCITO al limpiar (auditoría 27-ago R2 #6): con
      // `|| undefined` el JSON botaba la clave y el backend no desvinculaba nada.
      await backend.patch(`sii/facturas/${selectedF._id}/vincular`, {
        workOrderId:   vincForm.workOrderId || "",
        ocCliente:     vincForm.ocCliente || "",
        guiasDespacho: vincForm.guias
          ? vincForm.guias.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        // Fase B: siempre se manda (aunque vacío, para poder limpiar un reparto)
        repartos: vincForm.repartos
          .filter(r => r.workOrderId && Number(r.monto) > 0)
          .map(r => ({ workOrderId: r.workOrderId, monto: Number(r.monto) })),
      });
      await loadFacturas();
      setSelectedF(null);
    } catch (e) { alert("Error al vincular: " + (e?.response?.data?.error || e.message)); }
    setVincSaving(false);
  };

  // ── NC 61 (27-ago) ──
  const verPdfNc = async (folio) => {
    const w = window.open("", "_blank"); // antes del await (iOS)
    try {
      const res = await backend.get(`dte/emitido/${folio}/pdf`, { params: { tipo: 61 }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (w) w.location = url; else window.location = url;
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); alert("No se pudo abrir el PDF de la NC: " + (e?.message || "")); }
  };

  const emitirNc = async () => {
    if (!ncModal || ncModal.enviando || !selectedF) return;
    const cod = Number(ncModal.codigo);
    if (!ncModal.motivo.trim()) { setNcModal(m => ({ ...m, error: "Escribe el motivo (va como RazonRef en el DTE)" })); return; }
    let items;
    if (cod === 3) {
      const monto = Math.round(Number(ncModal.montoNeto));
      if (!(monto > 0)) { setNcModal(m => ({ ...m, error: "Para corregir montos indica el monto NETO de la corrección" })); return; }
      items = [{ nombre: `CORRECCION F${selectedF.folio}: ${ncModal.motivo}`.slice(0, 80), qty: 1, precio: monto }];
    }
    setNcModal(m => ({ ...m, enviando: true, error: null }));
    try {
      const r = await backend.post("dte/emitir-nc", { facturaId: selectedF._id, codigoRef: cod, motivo: ncModal.motivo, items });
      setNcModal(m => ({ ...m, enviando: false, resultado: r.data }));
      await loadFacturas();
    } catch (e) {
      setNcModal(m => ({ ...m, enviando: false, error: e?.response?.data?.error || e.message }));
    }
  };

  // ── Métricas ──
  // H-4 (levantamiento contable 25-ago): las notas de crédito (tipoDoc 61)
  // llegan del RCV con monto POSITIVO — para sumar RESTAN. Sin este signo, las
  // ventas y el IVA débito quedaban sobre-estimados (~$17,2M netos 2024-2026).
  // (Las notas de débito, tipoDoc 56, suman como factura.)
  const signo = f => (f.tipoDoc === 61 ? -1 : 1);
  const ventas   = facturas.filter(f => f.tipo === "venta");
  const compras  = facturas.filter(f => f.tipo === "compra");
  const totalVentas  = ventas.reduce((s, f) => s + signo(f) * (f.montoTotal || 0), 0);
  const totalCompras = compras.reduce((s, f) => s + signo(f) * (f.montoTotal || 0), 0);
  const ivaVentas    = ventas.reduce((s, f) => s + signo(f) * (f.iva || 0), 0);
  const ivaCompras   = compras.reduce((s, f) => s + signo(f) * (f.iva || 0), 0);
  const ivaPagar     = ivaVentas - ivaCompras;

  const rows = tab === "ventas" ? ventas : compras;

  // ── Filtros en cliente (28-ago): buscador + chips de estado + sin vincular ──
  const nPorEstado = rows.reduce((acc, f) => {
    const e = f.estado || "pendiente";
    acc[e] = (acc[e] || 0) + 1;
    return acc;
  }, {});
  const nSinVincular = rows.filter(f => !f.workOrder).length;
  const q = buscar.trim().toLowerCase();
  const rowsFiltradas = rows.filter(f => {
    if (soloSinVincular && f.workOrder) return false;
    if (estadoFiltro && (f.estado || "pendiente") !== estadoFiltro) return false;
    if (!q) return true;
    const razon = (tab === "ventas" ? f.razonReceptor : f.razonEmisor) || "";
    const rut   = (tab === "ventas" ? f.rutReceptor   : f.rutEmisor)   || "";
    return String(f.folio || "").toLowerCase().includes(q)
      || razon.toLowerCase().includes(q)
      || rut.toLowerCase().includes(q);
  });

  return (
    <div style={S.wrap}>

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:"#fff" }}>Contabilidad</div>
          <div style={{ fontSize:11, color: "#999", marginTop:2 }}>
            {estado?.conectado
              ? `RUT: ${estado.rut} · última sync: ${fmtD(estado.ultimaSync)}`
              : "Sin conexión SII — configurar en Ajustes"}
            {estado?.cupo && (
              // Cupo mensual de la llave gratis de SimpleAPI (30/mes). El botón se bloquea en 28.
              <span style={{ marginLeft: 8, color: estado.cupo.botonBloqueado ? "#e74c3c" : estado.cupo.usadas >= 20 ? "#f39c12" : "#999" }}
                    title={`Consultas a SimpleAPI este mes. El sync automático corre martes y viernes; el botón se bloquea al llegar a ${estado.cupo.bloqueoBoton}.`}>
                · SimpleAPI {estado.cupo.usadas}/{estado.cupo.limite} este mes{estado.cupo.botonBloqueado ? " · botón bloqueado" : ""}
              </span>
            )}
          </div>
        </div>
        {/* En móvil los controles bajan de línea y se reparten el ancho */}
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", width: isMobile ? "100%" : undefined }}>
          <select value={mes} onChange={e => setMes(e.target.value)} style={{ ...S.input, width:120, flex: isMobile ? "2 1 120px" : undefined }}>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map(m => (
              <option key={m} value={m}>
                {new Date(2000, Number(m)-1).toLocaleString("es-CL",{month:"long"})}
              </option>
            ))}
          </select>
          <select value={ano} onChange={e => setAno(e.target.value)} style={{ ...S.input, width:80, flex: isMobile ? "1 1 80px" : undefined }}>
            {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
          </select>
          <button
            onClick={handleSync}
            disabled={syncing || !estado?.conectado || estado?.cupo?.botonBloqueado}
            title={estado?.cupo?.botonBloqueado
              ? `Cupo mensual de SimpleAPI: ${estado.cupo.usadas} de ${estado.cupo.limite} consultas usadas. El botón se libera el 1 del mes siguiente; el sync automático (martes y viernes) sigue.`
              : undefined}
            className="t-02"
            style={{
              background: (syncing || estado?.cupo?.botonBloqueado) ? "#333" : "#e30613", color:"#fff", border:"none",
              borderRadius: isMobile ? 10 : 6, padding:"7px 16px", fontSize:12, fontWeight:700,
              cursor: (syncing || estado?.cupo?.botonBloqueado) ? "not-allowed" : "pointer", opacity: (syncing || estado?.cupo?.botonBloqueado) ? 0.7 : 1,
              flex: isMobile ? "1 1 100%" : undefined,
            }}>
            {syncing ? "Sincronizando…" : estado?.cupo?.botonBloqueado ? "⛔ Cupo SimpleAPI agotado" : "⟳ Sincronizar SII"}
          </button>
        </div>
      </div>

      {syncMsg && (
        <div style={{
          background: syncMsg.startsWith("✅") ? "#0d2a15" : "#2d1515",
          border: `1px solid ${syncMsg.startsWith("✅") ? "#2ecc71" : "#e74c3c"}`,
          borderRadius:6, padding:"8px 12px", fontSize:12,
          color: syncMsg.startsWith("✅") ? "#2ecc71" : "#e74c3c",
          marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center",
        }}>
          <span>{syncMsg}</span>
          <button onClick={() => setSyncMsg(null)} style={{ background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:14 }}>×</button>
        </div>
      )}

      {!estado?.conectado && (
        <div style={{
          background:"#1a1000", border:"1px solid #4a3500", borderRadius:6,
          padding:"10px 14px", fontSize:12, color:"#f39c12", marginBottom:14,
        }}>
          ⚠ Sin credenciales SII configuradas.
          Ve a <strong>Configuración → Integración SII</strong> para conectar.
        </div>
      )}

      {/* ── 4 métricas ── (móvil: 2×2 con números tabulares; ocultas en la
          vista por cliente, que trae su propio resumen multi-mes) */}
      {!["clientes", "piezas", "cuadre", "pagos", "cartolas", "libro", "activos", "finanzas", "clasificar", "costos"].includes(tab) && (
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap: isMobile ? "wrap" : undefined }}>
        {[
          { label:"Ventas del mes",  value:fmtM(totalVentas),  color:"#2ecc71", sub:`${ventas.length} facturas` },
          { label:"Compras del mes", value:fmtM(totalCompras), color:"#e74c3c", sub:`${compras.length} facturas` },
          { label:"IVA a pagar",     value:fmtM(ivaPagar),     color: ivaPagar >= 0 ? "#f39c12" : "#2ecc71", sub:"Ventas − Compras" },
          { label: soloSinVincular ? "Sin vincular ✕" : "Sin vincular",
            value: nSinVincular,
            color:"#a78bfa",
            sub: soloSinVincular ? "filtrando la tabla — toca para soltar" : "facturas sin OT asociada",
            click: () => setSoloSinVincular(v => !v),
            activa: soloSinVincular },
        ].map(m => (
          <div key={m.label}
            onClick={m.click || undefined}
            className={m.click ? "t-02 tap-card" : undefined}
            style={{ ...S.card, flex: isMobile ? "1 1 42%" : "1 1 0",
              cursor: m.click ? "pointer" : undefined,
              border: `1px solid ${m.activa ? "#a78bfa" : "#2e2e2e"}` }}>
            <div style={{ ...S.cardLabel, marginBottom:4 }}>{m.label}</div>
            <div style={{ fontSize: isMobile ? 19 : 22, fontWeight:700, color:m.color, fontVariantNumeric:"tabular-nums" }}>{m.value}</div>
            <div style={{ fontSize:10, color: "#999", marginTop:2 }}>{m.sub}</div>
          </div>
        ))}
      </div>
      )}

      {/* Las emisiones (antes pestaña "Emisiones ⚡") viven ahora en su propio módulo. */}
      <div style={{ fontSize:11, marginBottom:6 }}>
        <Link to="/facturacion" style={{ color:"#8a8a8a", textDecoration:"none" }}>Lo emitido desde el ERP está en Facturación →</Link>
      </div>

      {/* ── Tabs ── (móvil: carrusel si no caben los 3) */}
      <div className="chips-scroll" style={{ display:"flex", gap:6, marginBottom:10 }}>
        {[
          { key:"ventas",   label:"Facturas de Venta" },
          { key:"compras",  label:"Facturas de Compra" },
          { key:"clasificar", label:"Clasificar" },
          { key:"clientes", label:"Ventas por Cliente" },
          { key:"piezas",   label:"Ventas por Pieza" },
          { key:"cuadre",   label:"Cuadre" },
          { key:"pagos",    label:"Pagos" },
          { key:"cartolas", label:"Cartolas" },
          { key:"libro",    label:"Libro · F29" },
          { key:"activos",  label:"Activos" },
          { key:"finanzas", label:"Finanzas" },
          { key:"costos",   label:"Costos por OT" },
          { key:"correo",   label:"Correo contabilidad@" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={S.tab(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {/* ── Ventas por cliente / Cuadre ── (26-ago, área contable Fase A) */}
      {tab === "clientes" ? (
        <VentasPorCliente />
      ) : tab === "piezas" ? (
        <VentasPorPieza />
      ) : tab === "clasificar" ? (
        <ClasificarCompras />
      ) : tab === "cuadre" ? (
        <Cuadre />
      ) : tab === "pagos" ? (
        <Pagos />
      ) : tab === "cartolas" ? (
        <Cartolas />
      ) : tab === "libro" ? (
        <Libro />
      ) : tab === "activos" ? (
        <ActivosFijos />
      ) : tab === "finanzas" ? (
        <Finanzas />
      ) : tab === "costos" ? (
        <CostosResumen />
      ) : tab === "correo" ? (
        <CorreoContabilidad />
      ) : (
      <>
      {/* ── Buscador + chips de estado ── (28-ago: filtran en cliente) */}
      <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <input
          value={buscar}
          onChange={e => setBuscar(e.target.value)}
          placeholder="🔎 Buscar folio, razón social o RUT…"
          style={{ ...S.input, flex:"1 1 220px", minWidth:180 }}
        />
        {["acusado", "pendiente", "reclamado"].map(e => {
          const st = ESTADO_STYLE[e];
          const activo = estadoFiltro === e;
          return (
            <button key={e} type="button" className="chip-touch no-touch-min t-02"
              onClick={() => setEstadoFiltro(activo ? null : e)}
              style={{
                background: activo ? st.bg : "#1e1e1e",
                color: activo ? st.color : "#999",
                border: `1px solid ${activo ? st.color : "#333"}`,
                borderRadius:6, padding:"5px 12px", fontSize:11, cursor:"pointer", whiteSpace:"nowrap",
                fontVariantNumeric:"tabular-nums",
              }}>
              {st.label} ({nPorEstado[e] || 0}){activo ? " ✕" : ""}
            </button>
          );
        })}
        {soloSinVincular && (
          <button type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setSoloSinVincular(false)}
            style={{ background:"#221a33", color:"#a78bfa", border:"1px solid #a78bfa",
              borderRadius:6, padding:"5px 12px", fontSize:11, cursor:"pointer", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>
            Sin vincular ({nSinVincular}) ✕
          </button>
        )}
      </div>

      {/* ── Listado ── (móvil: tarjetas tocables; desktop: tabla como siempre) */}
      {isMobile ? (
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {loading ? (
          <div style={{ textAlign:"center", color:"#8a8a8a", fontSize:12, padding:"24px 0" }}>Cargando…</div>
        ) : rowsFiltradas.length === 0 ? (
          <div style={{ textAlign:"center", color:"#8a8a8a", fontSize:12, padding:"24px 0", background:"#232323", border:"1px solid #2e2e2e", borderRadius:12 }}>
            {rows.length === 0 ? `Sin facturas para ${periodo}. Toca "Sincronizar SII".` : "Sin facturas con esos filtros."}
          </div>
        ) : (
          rowsFiltradas.map((f, i) => (
            <div
              key={f._id || i}
              onClick={() => openPanel(f)}
              className="t-02 tap-card"
              style={{ background:"#232323", border:"1px solid #2e2e2e", borderRadius:12, padding:"12px 14px", cursor:"pointer" }}
            >
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <span style={{ fontFamily:"monospace", fontWeight:700, color: f.tipoDoc === 61 ? "#e74c3c" : "#3498db", fontSize:13, flexShrink:0 }}>
                  {f.tipoDoc === 61 ? "NC" : "F°"} {f.folio}
                </span>
                <span style={{ flex:1, fontSize:11, color:"#8a8a8a" }}>{fmtD(f.fecha)}</span>
                <Badge estado={f.estado} />
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:"#fff", marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {tab === "ventas" ? (f.razonReceptor || f.rutReceptor || "—") : (f.razonEmisor || f.rutEmisor || "—")}
              </div>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
                <span style={{ fontSize:11, color:"#8a8a8a", fontVariantNumeric:"tabular-nums" }}>
                  Neto {fmtM(f.montoNeto)} · IVA {fmtM(f.iva)}
                </span>
                <span style={{ fontSize:15, fontWeight:700, color: f.tipoDoc === 61 ? "#e74c3c" : "#2ecc71", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" }}>
                  {f.tipoDoc === 61 ? "−" : ""}{fmtM(f.montoTotal)}
                </span>
              </div>
              <div style={{ marginTop:8 }}>
                <span style={{
                  display:"inline-block", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20,
                  background: f.workOrder ? "#0d2a15" : "#2d1515",
                  color: f.workOrder ? "#2ecc71" : "#e74c3c",
                  border: `1px solid ${f.workOrder ? "#0d4020" : "#4a2020"}`,
                }}>
                  {f.workOrder ? `OT N°${f.workOrder.number}` : "Sin OT vinculada"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      ) : (
      <div style={{ border:"1px solid #2e2e2e", borderRadius:8, overflowX:"auto", background:"#242424", maxHeight:"calc(100vh - 340px)", overflowY:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, tableLayout:"fixed" }}>
          <colgroup>
            <col style={{ width:"70px"  }} />  {/* Folio */}
            <col style={{ width:"90px"  }} />  {/* Fecha */}
            <col />                             {/* Razón social */}
            <col style={{ width:"110px" }} />  {/* RUT */}
            <col style={{ width:"115px" }} />  {/* Neto */}
            <col style={{ width:"90px"  }} />  {/* IVA */}
            <col style={{ width:"120px" }} />  {/* Total */}
            <col style={{ width:"90px"  }} />  {/* Estado */}
            <col style={{ width:"80px"  }} />  {/* OT */}
          </colgroup>
          <thead style={{ position:"sticky", top:0, zIndex:2 }}>
            <tr>
              <th style={S.th}>Folio</th>
              <th style={S.th}>Fecha</th>
              <th style={S.th}>{tab === "ventas" ? "Cliente" : "Proveedor"}</th>
              <th style={S.th}>RUT</th>
              <th style={{ ...S.th, textAlign:"right" }}>Neto</th>
              <th style={{ ...S.th, textAlign:"right" }}>IVA</th>
              <th style={{ ...S.th, textAlign:"right" }}>Total</th>
              <th style={S.th}>Estado</th>
              <th style={S.th}>OT</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ ...S.td, textAlign:"center", color: "#999", padding:"28px 0" }}>Cargando…</td></tr>
            ) : rowsFiltradas.length === 0 ? (
              <tr><td colSpan={9} style={{ ...S.td, textAlign:"center", color: "#999", padding:"28px 0" }}>
                {rows.length === 0 ? `Sin facturas para ${periodo}. Haz clic en "Sincronizar SII".` : "Sin facturas con esos filtros."}
              </td></tr>
            ) : (
              rowsFiltradas.map((f, i) => (
                <tr
                  key={f._id || i}
                  onClick={() => openPanel(f)}
                  style={{ cursor:"pointer", background: i % 2 === 0 ? "#242424" : "#222" }}
                  onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background="#2a2a2a")}
                  onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background="")}
                >
                  <td style={{ ...S.td, fontFamily:"monospace", color: f.tipoDoc === 61 ? "#e74c3c" : "#3498db", fontWeight:700 }}>
                    {f.tipoDoc === 61 ? "NC " : ""}{f.folio}
                  </td>
                  <td style={S.td}>{fmtD(f.fecha)}</td>
                  <td style={{ ...S.td, color:"#fff" }}>
                    {tab === "ventas" ? (f.razonReceptor || f.rutReceptor || "—") : (f.razonEmisor || f.rutEmisor || "—")}
                  </td>
                  <td style={{ ...S.td, fontFamily:"monospace", color: "#bbb", fontSize:11 }}>
                    {tab === "ventas" ? f.rutReceptor : f.rutEmisor}
                  </td>
                  <td style={{ ...S.td, textAlign:"right" }}>{fmtM(f.montoNeto)}</td>
                  <td style={{ ...S.td, textAlign:"right", color:"#f39c12" }}>{fmtM(f.iva)}</td>
                  <td style={{ ...S.td, textAlign:"right", color: f.tipoDoc === 61 ? "#e74c3c" : "#2ecc71", fontWeight:700 }}>{f.tipoDoc === 61 ? "−" : ""}{fmtM(f.montoTotal)}</td>
                  <td style={{ ...S.td, overflow:"visible" }}><Badge estado={f.estado} /></td>
                  <td style={{ ...S.td, fontFamily:"monospace", color: f.workOrder ? "#2ecc71" : "#e74c3c", fontSize:11 }}>
                    {f.workOrder ? `N°${f.workOrder.number}` : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* ── Resumen IVA ── (móvil: a lo ancho de la pantalla) */}
      <div style={{ display:"flex", justifyContent:"flex-end", marginTop:10 }}>
        <div style={{ border:"1px solid #2e2e2e", borderRadius: isMobile ? 12 : 6, overflow:"hidden", minWidth:220, flex: isMobile ? 1 : undefined, background: isMobile ? "#232323" : undefined }}>
          {[
            { label:"IVA débito (ventas)",  val: ivaVentas,  color:"#e74c3c" },
            { label:"IVA crédito (compras)",val: ivaCompras, color:"#2ecc71" },
            { label:"IVA a pagar",          val: ivaPagar,   color: ivaPagar >= 0 ? "#f39c12" : "#2ecc71", bold:true },
          ].map(r => (
            <div key={r.label} style={{
              display:"flex", justifyContent:"space-between", padding:"6px 12px",
              fontSize:11, borderBottom:"1px solid #252525",
              background: r.bold ? "#2a2a2a" : "transparent",
            }}>
              <span style={{ color: "#aaa" }}>{r.label}</span>
              <span style={{ color:r.color, fontWeight: r.bold ? 700 : 400 }}>{fmtM(r.val)}</span>
            </div>
          ))}
        </div>
      </div>
      </>
      )}

      {/* ── Panel lateral de detalle ── */}
      {selectedF && (
        <div style={{ position:"fixed", inset:0, zIndex:1300, display:"flex", flexDirection:"row" }}>
          <div onClick={() => setSelectedF(null)} style={{ flex:1, background:"rgba(0,0,0,0.5)" }} />
          <div style={{ width:"min(460px, 92vw)", background:"#1e1e1e", borderLeft:"1px solid #2e2e2e", display:"flex", flexDirection:"column", overflowY:"auto", paddingBottom:"env(safe-area-inset-bottom)" }}>

            {/* header panel */}
            <div style={{ padding:"16px 18px", borderBottom:"1px solid #2e2e2e", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#fff" }}>
                  Factura {selectedF.tipo === "venta" ? "de Venta" : "de Compra"} N° {selectedF.folio}
                </div>
                <div style={{ fontSize:11, color: "#999", marginTop:2 }}>{fmtD(selectedF.fecha)} · {selectedF.periodo}</div>
              </div>
              <button onClick={() => setSelectedF(null)} style={{ background:"none", border:"none", color: "#999", fontSize:20, cursor:"pointer" }}>×</button>
            </div>

            <div style={{ padding:"16px 18px", flex:1 }}>
              {/* datos factura */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                {[
                  { label:"Razón Social",   val: selectedF.tipo === "venta" ? selectedF.razonReceptor : selectedF.razonEmisor },
                  { label:"RUT",            val: selectedF.tipo === "venta" ? selectedF.rutReceptor   : selectedF.rutEmisor  },
                  { label:"Monto Neto",     val: fmtM(selectedF.montoNeto) },
                  { label:"IVA",            val: fmtM(selectedF.iva) },
                  { label:"Total",          val: fmtM(selectedF.montoTotal) },
                  { label:"Estado SII",     val: selectedF.estado || "—" },
                ].map(d => (
                  <div key={d.label}>
                    <div style={{ fontSize:9, fontWeight:700, color: "#999", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3 }}>{d.label}</div>
                    <div style={{ fontSize:12, color:"#ddd" }}>{d.val || "—"}</div>
                  </div>
                ))}
              </div>

              {/* 27-ago: representación impresa del documento (PDF cosechado de
                  ventas, o render desde el XML rescatado para compras). */}
              <button
                onClick={async () => {
                  const w = window.open("", "_blank"); // antes del await (iOS)
                  try {
                    const res = await backend.get(`sii/facturas/${selectedF._id}/pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    if (w) w.location = url; else window.location = url;
                    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                  } catch (e) {
                    if (w) w.close();
                    let msg = e.message;
                    try { msg = JSON.parse(await e.response?.data?.text?.() || "{}").error || msg; } catch { /* blob */ }
                    alert(msg);
                  }
                }}
                style={{ width:"100%", background:"#0d2035", color:"#3498db", border:"1px solid #1a4a6e", borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:14, minHeight:42 }}>
                📄 Ver PDF del documento
              </button>

              {/* 27-ago: NC 61 sobre facturas de venta 33 emitidas por nosotros.
                  Las NC hijas del ERP llevan `referencia` a la factura madre
                  (solo se listan las del período cargado). */}
              {selectedF.tipo === "venta" && selectedF.tipoDoc === 33 && (() => {
                const ncHijas = facturas.filter(x =>
                  x.tipoDoc === 61 && x.referencia && Number(x.referencia.folio) === Number(selectedF.folio));
                return (
                  <div style={{ marginBottom:14 }}>
                    {ncHijas.length > 0 && (
                      <div style={{ marginBottom:8 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:"#e74c3c", textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>
                          Notas de crédito de esta factura
                        </div>
                        {ncHijas.map(nc => (
                          <div key={nc._id} style={{ display:"flex", alignItems:"center", gap:8, background:"#2d1515", border:"1px solid #4a2020", borderRadius:8, padding:"7px 10px", marginBottom:6 }}>
                            <span style={{ fontFamily:"monospace", fontWeight:700, color:"#e74c3c", fontSize:12, flexShrink:0 }}>NC {nc.folio}</span>
                            <span style={{ flex:1, fontSize:11, color:"#bbb", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {fmtD(nc.fecha)} · {nc.referencia.codigo === 1 ? "anula" : nc.referencia.codigo === 2 ? "corrige texto" : "corrige montos"} · −{fmtM(nc.montoTotal)}
                            </span>
                            <button onClick={() => verPdfNc(nc.folio)}
                              style={{ background:"none", border:"1px solid #4a2020", color:"#e74c3c", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", flexShrink:0 }}>
                              PDF
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setNcModal({ codigo:"1", motivo:"", montoNeto:"", enviando:false, resultado:null, error:null })}
                      style={{ width:"100%", background:"#2d1515", color:"#e74c3c", border:"1px solid #4a2020", borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:"pointer", minHeight:42 }}>
                      🧾 Emitir Nota de Crédito…
                    </button>
                  </div>
                );
              })()}

              {/* Panel de COMPRA (26-ago, feedback Maxi): una compra no se vincula
                  a OTs de cliente — lo que corresponde es su PAGO. */}
              {selectedF.tipo === "compra" && (
                <div style={{ borderTop:"1px solid #2e2e2e", paddingTop:14 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>
                    Pago de esta compra
                  </div>
                  {selectedF.pago?.pagada ? (
                    <div style={{ background:"#0d2a15", border:"1px solid #0d4020", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#2ecc71", marginBottom:12 }}>
                      ✓ Pagada el {fmtD(selectedF.pago.fecha)}{selectedF.pago.medio ? ` · ${selectedF.pago.medio}` : ""}
                    </div>
                  ) : (
                    <div style={{ background:"#2d2000", border:"1px solid #4a3500", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#f39c12", marginBottom:12 }}>
                      Pendiente de pago — el vencimiento y el semáforo están en la pestaña <b>Pagos</b>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      // Marcar pagada → mini-modal con presets (28-ago, adiós window.prompt)
                      if (!selectedF.pago?.pagada) { setPagoModal(selectedF); return; }
                      try {
                        await backend.patch(`sii/facturas/${selectedF._id}/pago`, { pagada: false, medio: "" });
                        await loadFacturas();
                        setSelectedF(null);
                      } catch (e) { alert(e?.response?.data?.error || e.message); }
                    }}
                    style={{ width:"100%", background: selectedF.pago?.pagada ? "#2a2a2a" : "#0d2a15", color: selectedF.pago?.pagada ? "#999" : "#2ecc71", border:"1px solid #0d4020", borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:"pointer", marginBottom:8 }}>
                    {selectedF.pago?.pagada ? "Despagar (corregir)" : "✓ Marcar como pagada"}
                  </button>
                  <div style={{ fontSize:10, color:"#8a8a8a" }}>
                    El acuse de recibo al SII hoy se hace por Enternet — quedará automático en el ERP con la integración de recibidos de SimpleFactura.
                  </div>
                </div>
              )}

              {/* Costeo por OT (27-ago): atribuir la COMPRA a trabajos — OPCIONAL, no bloqueante */}
              {selectedF.tipo === "compra" && (
              <div style={{ borderTop:"1px solid #2e2e2e", paddingTop:14, marginTop:14 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>
                  Atribuir a trabajo (costeo por OT) — opcional
                </div>
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color:"#999", marginBottom:4 }}>N° Trabajo (OT)</div>
                  <select
                    value={vincForm.workOrderId}
                    onChange={e => setVincForm(p => ({ ...p, workOrderId: e.target.value }))}
                    style={{ ...S.input, width:"100%" }}>
                    <option value="">— Sin atribuir (gasto general) —</option>
                    {workOrders.map(wo => (
                      <option key={wo._id} value={wo._id}>
                        N°{wo.number} — {wo.name || wo.client?.name || "—"} ({wo.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ borderTop:"1px dashed #333", paddingTop:12, marginBottom:6 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#f39c12", textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>
                    ¿El gasto sirve a varias OTs? Repartir el neto
                  </div>
                  {vincForm.repartos.map((r, i) => (
                    <div key={i} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"center" }}>
                      <select
                        value={r.workOrderId}
                        onChange={e => setVincForm(p => { const n=[...p.repartos]; n[i]={...n[i], workOrderId:e.target.value}; return {...p, repartos:n}; })}
                        style={{ ...S.input, flex:1, minWidth:0 }}>
                        <option value="">— OT —</option>
                        {workOrders.map(wo => (
                          <option key={wo._id} value={wo._id}>N°{wo.number} — {(wo.name || "").slice(0,25)}</option>
                        ))}
                      </select>
                      <input
                        type="number" min={0} placeholder="$ neto"
                        value={r.monto}
                        onChange={e => setVincForm(p => { const n=[...p.repartos]; n[i]={...n[i], monto:e.target.value}; return {...p, repartos:n}; })}
                        style={{ ...S.input, width:110, textAlign:"right", fontVariantNumeric:"tabular-nums" }}
                      />
                      <button type="button"
                        onClick={() => setVincForm(p => ({ ...p, repartos: p.repartos.filter((_, idx) => idx !== i) }))}
                        style={{ width:28, height:28, border:"none", background:"#333", color:"#cbd5e1", borderRadius:5, cursor:"pointer", flexShrink:0 }}>×</button>
                    </div>
                  ))}
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <button type="button"
                      onClick={() => setVincForm(p => ({ ...p, repartos: [...p.repartos, { workOrderId:"", monto:"" }] }))}
                      style={{ background:"#2a2a2a", color:"#cbd5e1", border:"1px dashed #3a3a3a", borderRadius:5, padding:"5px 10px", fontSize:11, cursor:"pointer" }}>
                      + Agregar OT al reparto
                    </button>
                    {vincForm.repartos.length > 0 && (() => {
                      const suma = vincForm.repartos.reduce((s, r) => s + (Number(r.monto) || 0), 0);
                      const neto = Number(selectedF.montoNeto) || 0;
                      const ok = Math.abs(suma - neto) <= 10;
                      return (
                        <span style={{ fontSize:11, color: ok ? "#2ecc71" : (suma > neto ? "#e74c3c" : "#f39c12"), fontVariantNumeric:"tabular-nums" }}>
                          {fmtM(suma)} de {fmtM(neto)}{ok ? " ✓" : suma > neto ? " — se pasa" : " — falta"}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize:10, color:"#8a8a8a", marginTop:8 }}>
                    Opcional: sin OT la compra sigue igual en Pagos, F29 y asientos. El margen por trabajo queda en Trabajos → 💰 Costos.
                  </div>
                </div>
              </div>
              )}

              {/* vinculación — SOLO ventas */}
              {selectedF.tipo === "venta" && (
              <div style={{ borderTop:"1px solid #2e2e2e", paddingTop:14 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#e30613", textTransform:"uppercase", letterSpacing:".07em", marginBottom:10 }}>
                  Vincular con trabajo del taller
                </div>

                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color: "#999", marginBottom:4 }}>N° Trabajo (OT)</div>
                  <select
                    value={vincForm.workOrderId}
                    onChange={e => setVincForm(p => ({ ...p, workOrderId: e.target.value }))}
                    style={{ ...S.input, width:"100%" }}>
                    <option value="">— Sin vincular —</option>
                    {workOrders.map(wo => (
                      <option key={wo._id} value={wo._id}>
                        N°{wo.number} — {wo.name || wo.client?.name || "—"} ({wo.status})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color: "#999", marginBottom:4 }}>N° OC del cliente</div>
                  <input
                    value={vincForm.ocCliente}
                    onChange={e => setVincForm(p => ({ ...p, ocCliente: e.target.value }))}
                    placeholder="Ej: OC-45242"
                    style={{ ...S.input, width:"100%" }}
                  />
                </div>

                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:10, color: "#999", marginBottom:4 }}>Guías de despacho (separadas por coma)</div>
                  <input
                    value={vincForm.guias}
                    onChange={e => setVincForm(p => ({ ...p, guias: e.target.value }))}
                    placeholder="G-2283, G-2284"
                    style={{ ...S.input, width:"100%" }}
                  />
                </div>

                {/* Fase B (26-ago): reparto del neto entre varias OTs — para
                    facturas que cubren más de un trabajo. Con reparto activo,
                    el cuadre asigna a cada OT solo su parte. */}
                {selectedF.tipo === "venta" && (
                <div style={{ borderTop:"1px dashed #333", paddingTop:12, marginBottom:14 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#f39c12", textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>
                    ¿Cubre varias OTs? Repartir el neto
                  </div>
                  {vincForm.repartos.map((r, i) => (
                    <div key={i} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"center" }}>
                      <select
                        value={r.workOrderId}
                        onChange={e => setVincForm(p => { const n=[...p.repartos]; n[i]={...n[i], workOrderId:e.target.value}; return {...p, repartos:n}; })}
                        style={{ ...S.input, flex:1, minWidth:0 }}>
                        <option value="">— OT —</option>
                        {workOrders.map(wo => (
                          <option key={wo._id} value={wo._id}>N°{wo.number} — {(wo.name || "").slice(0,25)}</option>
                        ))}
                      </select>
                      <input
                        type="number" min={0} placeholder="$ neto"
                        value={r.monto}
                        onChange={e => setVincForm(p => { const n=[...p.repartos]; n[i]={...n[i], monto:e.target.value}; return {...p, repartos:n}; })}
                        style={{ ...S.input, width:110, textAlign:"right", fontVariantNumeric:"tabular-nums" }}
                      />
                      <button type="button"
                        onClick={() => setVincForm(p => ({ ...p, repartos: p.repartos.filter((_, idx) => idx !== i) }))}
                        style={{ width:28, height:28, border:"none", background:"#333", color:"#cbd5e1", borderRadius:5, cursor:"pointer", flexShrink:0 }}>×</button>
                    </div>
                  ))}
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <button type="button"
                      onClick={() => setVincForm(p => ({ ...p, repartos: [...p.repartos, { workOrderId:"", monto:"" }] }))}
                      style={{ background:"#2a2a2a", color:"#cbd5e1", border:"1px dashed #3a3a3a", borderRadius:5, padding:"5px 10px", fontSize:11, cursor:"pointer" }}>
                      + Agregar OT al reparto
                    </button>
                    {vincForm.repartos.length > 0 && (() => {
                      const suma = vincForm.repartos.reduce((s, r) => s + (Number(r.monto) || 0), 0);
                      const neto = Number(selectedF.montoNeto) || 0;
                      const ok = Math.abs(suma - neto) <= 10;
                      return (
                        <span style={{ fontSize:11, color: ok ? "#2ecc71" : (suma > neto ? "#e74c3c" : "#f39c12"), fontVariantNumeric:"tabular-nums" }}>
                          {fmtM(suma)} de {fmtM(neto)}{ok ? " ✓" : suma > neto ? " — se pasa" : " — falta"}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                )}
              </div>
              )}
            </div>

            <div style={{ padding:"14px 18px", borderTop:"1px solid #2e2e2e", display:"flex", gap:8 }}>
              {(selectedF.tipo === "venta" || selectedF.tipo === "compra") && (
              <button
                onClick={handleVincular}
                disabled={vincSaving}
                style={{
                  flex:2, background: vincSaving ? "#333" : "#e30613",
                  color:"#fff", border:"none", borderRadius:6, padding:"8px",
                  fontSize:12, fontWeight:700, cursor: vincSaving ? "not-allowed" : "pointer",
                }}>
                {vincSaving ? "Guardando…" : "Guardar vinculación"}
              </button>
              )}
              <button onClick={() => setSelectedF(null)}
                style={{ flex:1, background:"transparent", color: "#cbd5e1", border:"1px solid #444", borderRadius:6, padding:"8px", fontSize:12, cursor:"pointer" }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mini-modal NC 61 (27-ago) ── */}
      {ncModal && selectedF && (
        <div style={{ position:"fixed", inset:0, zIndex:1400, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:12 }}
          onClick={() => !ncModal.enviando && setNcModal(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ width:"min(440px, 94vw)", maxHeight:"90vh", overflowY:"auto", background:"#1e1e1e", border:"1px solid #2e2e2e", borderRadius:12, padding:18 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <div style={{ fontSize:15, fontWeight:800, color:"#fff" }}>Nota de Crédito · F° {selectedF.folio}</div>
              <button onClick={() => setNcModal(null)} style={{ background:"none", border:"none", color:"#999", fontSize:20, cursor:"pointer" }}>×</button>
            </div>
            <div style={{ fontSize:11, color:"#8a8a8a", marginBottom:12 }}>
              {selectedF.razonReceptor || selectedF.rutReceptor} · Total {fmtM(selectedF.montoTotal)}
            </div>

            {ncModal.resultado ? (
              <div style={{ textAlign:"center", padding:"10px 0" }}>
                <div style={{ fontSize:32 }}>✅</div>
                <div style={{ fontSize:14, fontWeight:800, color:"#2ecc71", margin:"6px 0" }}>
                  {ncModal.resultado.prueba ? "NC de PRUEBA emitida" : "Nota de crédito emitida"}
                  {ncModal.resultado.folio ? ` — Folio ${ncModal.resultado.folio}` : ""}
                </div>
                <div style={{ fontSize:12, color:"#b8b8b8", fontVariantNumeric:"tabular-nums" }}>
                  Neto {fmtM(ncModal.resultado.neto)} · IVA {fmtM(ncModal.resultado.iva)} · Total {fmtM(ncModal.resultado.total)}
                </div>
                {ncModal.resultado.reversa && (
                  <div style={{ fontSize:11, color:"#f39c12", marginTop:6 }}>
                    {ncModal.resultado.reversa.manual
                      ? "⚠ La factura no tenía detalle guardado: hay que revertir los saldos facturados A MANO."
                      : `Saldos revertidos en ${ncModal.resultado.reversa.revertidas} pieza(s)${ncModal.resultado.reversa.descuadres ? ` · ${ncModal.resultado.reversa.descuadres} descuadre(s) — revisar log del servidor` : ""}.`}
                  </div>
                )}
                <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:14 }}>
                  {ncModal.resultado.folio && (
                    <button onClick={() => verPdfNc(ncModal.resultado.folio)}
                      style={{ ...S.input, cursor:"pointer" }}>📄 Ver PDF timbrado</button>
                  )}
                  <button onClick={() => setNcModal(null)} style={{ ...S.input, cursor:"pointer" }}>Cerrar</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color:"#999", marginBottom:4 }}>Tipo de corrección (CodRef SII)</div>
                  <select value={ncModal.codigo} onChange={e => setNcModal(m => ({ ...m, codigo: e.target.value }))} style={{ ...S.input, width:"100%" }}>
                    <option value="1">1 — Anula la factura completa</option>
                    <option value="2">2 — Corrige texto (sin montos)</option>
                    <option value="3">3 — Corrige montos</option>
                  </select>
                </div>
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, color:"#999", marginBottom:4 }}>Motivo (RazonRef)</div>
                  <textarea rows={2} value={ncModal.motivo} onChange={e => setNcModal(m => ({ ...m, motivo: e.target.value }))}
                    placeholder="Ej: anula por error en el cliente / corrige glosa / corrige precio unitario…"
                    style={{ ...S.input, width:"100%", resize:"vertical" }} />
                </div>
                {ncModal.codigo === "3" && (
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:10, color:"#999", marginBottom:4 }}>Monto NETO de la corrección ($)</div>
                    <input type="number" min={0} value={ncModal.montoNeto}
                      onChange={e => setNcModal(m => ({ ...m, montoNeto: e.target.value }))}
                      style={{ ...S.input, width:"100%" }} />
                    <div style={{ fontSize:10, color:"#8a8a8a", marginTop:3 }}>
                      La NC sale con una línea única por ese neto + IVA (el motivo va de glosa).
                    </div>
                  </div>
                )}
                {ncModal.codigo === "1" && (
                  <div style={{ background:"#2d1515", border:"1px solid #e74c3c", borderRadius:8, padding:"9px 12px", fontSize:11, color:"#e74c3c", fontWeight:700, marginBottom:10 }}>
                    ⚠ ANULACIÓN TOTAL: la NC anula la factura {selectedF.folio} completa ante el SII y
                    REVIERTE los saldos facturados de las piezas de la OT (vuelven a quedar por facturar).
                    Esta acción no se puede deshacer desde el ERP.
                  </div>
                )}
                {ncModal.error && <div style={{ color:"#e74c3c", fontSize:12, marginBottom:8 }}>{ncModal.error}</div>}
                <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                  <button onClick={() => setNcModal(null)} disabled={ncModal.enviando} style={{ ...S.input, cursor:"pointer" }}>Cancelar</button>
                  <button onClick={emitirNc} disabled={ncModal.enviando}
                    style={{ background: ncModal.enviando ? "#333" : "#e30613", color:"#fff", border:"none", borderRadius:8, padding:"9px 18px", fontSize:13, fontWeight:700, cursor: ncModal.enviando ? "not-allowed" : "pointer" }}>
                    {ncModal.enviando ? "Emitiendo…" : ncModal.codigo === "1" ? "Sí, ANULAR con NC" : "Emitir NC"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Mini-modal medio de pago (28-ago) ── */}
      <MedioPagoModal
        open={!!pagoModal}
        titulo={pagoModal ? `Marcar pagada · F° ${pagoModal.folio}` : ""}
        subtitulo={pagoModal ? `${pagoModal.razonEmisor || pagoModal.rutEmisor || "—"} · ${fmtM(pagoModal.montoTotal)}` : ""}
        onCancel={() => setPagoModal(null)}
        onConfirm={async medio => {
          try {
            await backend.patch(`sii/facturas/${pagoModal._id}/pago`, { pagada: true, medio });
            setPagoModal(null);
            await loadFacturas();
            setSelectedF(null);
          } catch (e) { alert(e?.response?.data?.error || e.message); }
        }}
      />
    </div>
  );
};

export default Contabilidad;
